import { useEffect, useState } from "react";
import { api, COUNTRY_FLAGS } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowRight, Check, Zap } from "lucide-react";

const CATEGORIES_PREVIEW = [
  "Energy Policy", "Key Publications", "Grid Fees & Tariffs", "Capacity Market",
  "Subsidy Schemes", "Grid Connection", "Tenders", "Connection Queue",
  "BESS Market Participation", "PICASSO / MARI",
];

export default function Landing() {
  const [countries, setCountries] = useState([]);
  const [selected, setSelected] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data.countries)).catch(() => {});
  }, []);

  const toggle = (code) =>
    setSelected((s) => (s.includes(code) ? s.filter((c) => c !== code) : [...s, code]));

  const submit = async (e) => {
    e.preventDefault();
    if (!email || selected.length === 0) {
      toast.error("Enter your email and pick at least one country.");
      return;
    }
    if (!email.toLowerCase().endsWith("@cleanhorizon.com")) {
      toast.error("Only @cleanhorizon.com email addresses can subscribe.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/subscribe", { email, countries: selected });
      toast.success(`Subscribed to ${selected.length} countr${selected.length > 1 ? "ies" : "y"}. Briefs land every other Monday at 08:00 CET.`);
      setEmail("");
      setSelected([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]">
      <SiteHeader />

      {/* Hero */}
      <section className="relative border-b border-[#E5E5E5] overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1548337138-e87d889cc369?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHx3aW5kJTIwdHVyYmluZXMlMjBjbGVhbiUyMGVuZXJneXxlbnwwfHx8fDE3ODM1MzQxNjB8MA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#FAFAFA]/85" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 py-24 lg:py-32">
          <div className="flex items-center gap-2 mb-6 text-xs tracking-[0.2em] uppercase font-semibold">
            <span className="bg-volt text-black px-2 py-1 flex items-center gap-1"><Zap className="w-3 h-3" strokeWidth={3} /> Bi-Weekly Intelligence</span>
            <span className="text-[#737373]">17 European Markets · Native-language research</span>
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[0.9] max-w-4xl">
            EUROPE'S ENERGY POLICY,<br />DECODED FOR <span className="bg-volt px-2">BESS</span>.
          </h1>
          <p className="mt-8 max-w-2xl text-base sm:text-lg text-[#404040] leading-relaxed">
            Every other Monday, your inbox gets a sharp recap of energy-policy news across your chosen
            markets — capacity market rules, grid fees, tenders, connection queues, subsidy schemes,
            and BESS market participation. Researched in English <em>and</em> each country's native
            language. Every item cited with clickable sources.
          </p>
        </div>
      </section>

      {/* Country marquee */}
      <div className="border-b border-[#E5E5E5] bg-[#0A0A0A] text-[#FAFAFA] overflow-hidden py-3">
        <div className="flex gap-8 animate-marquee whitespace-nowrap font-mono text-xs tracking-widest uppercase">
          {[...countries, ...countries].map((c, i) => (
            <span key={i} className="flex items-center gap-2">
              <span className="text-volt">◆</span> {COUNTRY_FLAGS[c.code]} {c.name}
            </span>
          ))}
        </div>
      </div>

      {/* Subscribe */}
      <section className="max-w-[1400px] mx-auto px-6 py-20 grid lg:grid-cols-[1fr_1.4fr] gap-16">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#737373] mb-4">01 / Sign up</div>
          <h2 className="font-display font-bold tracking-tight text-3xl lg:text-4xl mb-6">Pick your markets.</h2>
          <p className="text-[#404040] mb-8 leading-relaxed">
            Select every country you want covered. You'll receive one consolidated brief per market
            every other Monday at 08:00 CET.
          </p>
          <form onSubmit={submit} className="space-y-4" data-testid="subscribe-form">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-semibold block mb-2">Work email (@cleanhorizon.com only)</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@cleanhorizon.com"
                className="rounded-none border-2 border-[#0A0A0A] bg-white h-12 text-base focus-visible:ring-2 focus-visible:ring-volt focus-visible:ring-offset-0"
                data-testid="email-input"
              />
            </div>
            <div className="text-sm text-[#737373]" data-testid="selected-count">
              {selected.length} countr{selected.length === 1 ? "y" : "ies"} selected
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-volt text-black font-display font-bold tracking-tight uppercase flex items-center justify-center gap-2 border-2 border-black transition-colors hover:bg-black hover:text-volt disabled:opacity-50"
              data-testid="subscribe-button"
            >
              {loading ? "Submitting…" : "Subscribe"} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>

        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#737373] mb-4">02 / 17 markets</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-0 border-l border-t border-[#E5E5E5]">
            {countries.map((c) => {
              const active = selected.includes(c.code);
              return (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => toggle(c.code)}
                  className={`relative border-r border-b border-[#E5E5E5] p-4 text-left transition-colors ${active ? "bg-volt text-black" : "bg-white hover:bg-[#f0f0f0]"}`}
                  data-testid={`country-select-${c.code}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xl">{COUNTRY_FLAGS[c.code]}</span>
                    {active && <Check className="w-4 h-4" strokeWidth={3} />}
                  </div>
                  <div className="mt-3 font-display font-bold text-sm tracking-tight leading-tight">{c.name}</div>
                  <div className="font-mono text-[10px] text-[#737373] mt-1">{c.code} · {c.languages.join(", ")}</div>
                </button>
              );
            })}
          </div>

          <div className="mt-10">
            <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#737373] mb-3">What every brief covers</div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES_PREVIEW.map((cat) => (
                <span key={cat} className="font-mono text-xs border border-[#0A0A0A] px-2 py-1">{cat}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E5E5E5] py-8 text-center text-xs text-[#737373] font-mono tracking-widest uppercase">
        BESS Brief · Bi-Weekly European Energy Policy Intelligence
      </footer>
    </div>
  );
}
